import { useRef } from 'react'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectActiveContentViewerContextMatches } from '@/content-viewer/store/selectors'
import { ViewerOpenContextKind } from '@/content-viewer/store/schemas'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { RiaSourceTriggerLocation } from '@/analytics/enums/ria.enums'
import { CitatorPanelSource, CitatorSurface, mapCaseStatusToBadgeType } from '@/analytics/enums/citator.enums'
import { trackCitatorBadgeClicked, trackCitatorBadgeImpression, trackCitatorPanelOpened } from '@/analytics/utils/citator-analytics'
import { selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { isDisplayableCaseStatus } from '@/citator/schema/case-status-schema'
import { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { agentReferenceMetadataToTitle, getCaseStatusFromReference } from '../util/agent-reference-title'
import { useOpenAgentReferenceViewer } from './useOpenAgentReferenceViewer'

type UseCitationSourceTooltipParams = {
  citation_id: string
  citation_id_index: number
  /** Analytics: which citation element triggered the open. */
  triggerLocation: RiaSourceTriggerLocation
  surface: CitatorSurface
}

/**
 * Citation interaction for chat: clicking opens the content viewer at this occurrence's
 * highlights, and hovering (~1s) shows the source tooltip — title, metadata, and the
 * citator case-status treatment (PROD-699). Shared by the legal citation link and the
 * numbered badge so both behave identically.
 */
export function useCitationSourceTooltip(params: UseCitationSourceTooltipParams) {
  const { citation_id, citation_id_index, triggerLocation, surface } = params
  const { trackEvent } = useAnalytics()
  const { openReference: openReferenceViewer, isOpening, error, clearError } = useOpenAgentReferenceViewer()

  const reference = useAppSelector((state: RootState) => state.agentReferencesState.references[citation_id]) ?? null
  const matterId = useAppSelector((state: RootState) =>
    reference ? selectAgentConversationMatterId(state, { conversationId: reference.conversation_id }) : null
  )

  // Whether this occurrence is the one open in the content viewer
  const isActive = useAppSelector((state: RootState) =>
    selectActiveContentViewerContextMatches(state, {
      kind: ViewerOpenContextKind.CHAT_CITATION,
      citationId: citation_id,
      highlightIndex: citation_id_index,
    })
  )

  const title = reference ? agentReferenceMetadataToTitle(reference) : undefined
  const caseStatus = reference ? getCaseStatusFromReference(reference) : undefined
  const displayableBadgeType = isDisplayableCaseStatus(caseStatus) ? mapCaseStatusToBadgeType(caseStatus) : null

  // Impression = treatment visible, i.e. the tooltip opened (hover, ~1s delay).
  // Guard fires once per badgeType per element, matching the retired popover.
  const lastImpressionKeyRef = useRef<string | null>(null)
  const handleTooltipOpen = () => {
    clearError()
    if (!displayableBadgeType || lastImpressionKeyRef.current === displayableBadgeType) return
    lastImpressionKeyRef.current = displayableBadgeType
    trackCitatorBadgeImpression({ trackEvent, surface, badgeType: displayableBadgeType })
  }

  const openCitationReference = async (options: { citationHighlightIndex?: number; defaultTab?: CaseLawReferenceTab }) => {
    if (!reference) return false
    return openReferenceViewer({
      reference,
      matterId,
      triggerLocation,
      ...options,
    })
  }

  /** Click on the citation itself or the tooltip's title: open the source at this occurrence. */
  const openCitation = () => {
    void openCitationReference({ citationHighlightIndex: citation_id_index })
  }

  /** Click on the tooltip's treatment tag: open the source on the citator tab. */
  const handleViewCitationAnalysis = async () => {
    // The badge is the panel CTA here; the panel-opened event fires only when the viewer opens.
    if (displayableBadgeType) {
      trackCitatorBadgeClicked({ trackEvent, surface, badgeType: displayableBadgeType })
    }

    const opened = await openCitationReference({ citationHighlightIndex: citation_id_index, defaultTab: CaseLawReferenceTab.citator })
    if (opened && displayableBadgeType) {
      trackCitatorPanelOpened({
        trackEvent,
        surface,
        source: CitatorPanelSource.BadgeClick,
      })
    }
  }

  return {
    reference,
    isActive,
    title,
    caseStatus,
    isOpening,
    error,
    handleTooltipOpen,
    openCitation,
    handleViewCitationAnalysis,
  }
}
